import React from 'react';
import {
  Box, Button, CircularProgress, Divider, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import FormActionBar from '../../../shared/components/FormActionBar';
import AWSConsoleButton from '../../../shared/components/AWSConsoleButton';
import AWSCliCredentials from '../../../shared/components/AWSCliCredentials';
import type { AwsAccount, Customer } from '../../../types/backend';
import { ACCESS_FIELDS, validateAccountForm, type useAccountForm } from '../hooks/useAccountForm';
import { useAccountSectionSave } from '../hooks/useAccountSectionSave';
import { useAccountBaselineStatus } from '../hooks/useAccountBaselineStatus';
import RoleTestResultPanel from './RoleTestResultPanel';
import SectionSaveOutcome from './SectionSaveOutcome';
import { externalIdNote, reachabilityText } from '../accountTabs';

/** Read from the status the onboarding panel already loads; only mounted while account_baseline is on. */
const Reachability: React.FC<{ customerId: string; awsAccountId: string }> = ({ customerId, awsAccountId }) => {
  const { accounts, loading, error } = useAccountBaselineStatus(customerId);
  const view = accounts.find((a) => a.aws_account_id === awsAccountId);
  // Three different facts, never one fallback: the read failed, is still
  // running, or came back without this account.
  const text = error ? 'Could not read' : view ? reachabilityText(view) : loading ? 'Reading' : 'Not checked yet';
  return (
    <Box>
      <Typography component="dt" variant="caption" color="text.secondary">Reachability</Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>{text}</Typography>
    </Box>
  );
};

type TestableRole = 'readonly' | 'write' | 'automation';

interface RoleFieldSpec {
  field: 'read_role_name' | 'write_role_name' | 'automation_role_name' | 'onboarding_role_name';
  label: string;
  placeholder: string;
  helper: string;
  /** Named administrator roles: the server takes them from an admin only. */
  adminOnly?: boolean;
  /** The role the Test button assumes; the onboarding role has none. */
  test?: TestableRole;
}

const ROLE_FIELDS: RoleFieldSpec[] = [
  { field: 'read_role_name', label: 'Read-Only Role Name *', placeholder: 'cloud2-operator-readonly',
    helper: 'IAM role name for read-only access (investigations)', test: 'readonly' },
  { field: 'write_role_name', label: 'Write Role Name', placeholder: 'cloud2-operator-admin',
    helper: 'IAM role name for write access (remediation) - optional', test: 'write' },
  { field: 'automation_role_name', label: 'Automation Role Name', placeholder: 'cloud2-operator-automation',
    helper: 'IAM role for automation (Lambda invoke, SSM, Service Catalog) - optional', adminOnly: true, test: 'automation' },
  { field: 'onboarding_role_name', label: 'Onboarding Role Name', placeholder: 'cloud2-onboarding',
    helper: 'Installed by the customer before onboarding starts. OpsPilot assumes it to create the other roles.', adminOnly: true },
];

/** The role names, the external ID and the event bus, edited and saved here and nowhere else. */
const AccessForm: React.FC<{
  form: ReturnType<typeof useAccountForm>;
  allowAdminRoles: boolean;
}> = ({ form, allowAdminRoles }) => {
  const section = useAccountSectionSave(form, ACCESS_FIELDS);
  // The backend validates the test region against the account's persisted
  // supported_regions, so the buttons send the saved region, not a draft.
  const savedRegion = form.selectedEntity?.main_region;
  // The form's own rules, read live only to hold Test back on a name that
  // cannot be valid; what the operator is told waits for Save.
  const draftErrors = validateAccountForm(form.formData);

  return (
    <Box component="section" aria-label="Access settings" sx={{ display: 'grid', gap: 2.5 }}>
      {ROLE_FIELDS.map((spec) => {
        const value = form.formData[spec.field];
        const error = section.fieldError(spec.field);
        const locked = spec.adminOnly && !allowAdminRoles;
        const testRole = spec.test;
        return (
          <Box key={spec.field} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label={spec.label}
              value={value}
              onChange={(e) => form.handleChange(spec.field, e.target.value)}
              placeholder={spec.placeholder}
              disabled={locked}
              error={error !== null}
              helperText={error || (locked ? 'Only an admin may change this' : spec.helper)}
              InputLabelProps={{ shrink: true }}
            />
            {testRole && (
              <Button
                variant="contained"
                startIcon={form.testing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                onClick={() => form.handleTestRole(testRole, savedRegion)}
                disabled={form.testing || !value.trim() || Boolean(draftErrors[spec.field])}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
              >
                Test
              </Button>
            )}
          </Box>
        );
      })}
      {form.testResult && <RoleTestResultPanel testResult={form.testResult} />}
      <TextField
        fullWidth
        label="External ID"
        value={form.formData.external_id}
        onChange={(e) => form.handleChange('external_id', e.target.value)}
        placeholder="optional-external-id"
        error={section.fieldError('external_id') !== null}
        helperText={section.fieldError('external_id')
          || 'Optional external ID for security. The saved one and whether it is the default show below.'}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        fullWidth
        label="Event Bus Name"
        value={form.formData.event_bus_name}
        onChange={(e) => form.handleChange('event_bus_name', e.target.value)}
        placeholder="cloud2-events"
        error={section.fieldError('event_bus_name') !== null}
        helperText={section.fieldError('event_bus_name') || 'EventBridge bus name for Service Catalog automation events'}
        InputLabelProps={{ shrink: true }}
      />
      <SectionSaveOutcome section={section} />
      <FormActionBar onSave={section.save} saving={section.saving} saveDisabled={section.saving || !section.dirty} sx={{ mt: 0 }} />
    </Box>
  );
};

export interface AccountAccessTabProps {
  customer: Customer;
  account: AwsAccount;
  baselineEnabled: boolean;
  form: ReturnType<typeof useAccountForm>;
  /** customers:UpdateAWSAccount's level (L2 and up): the role names, external ID and event bus. */
  canEdit: boolean;
  /** Admin: the automation and onboarding role names. */
  allowAdminRoles: boolean;
}

/** The roles Cloud2 uses in the account, how it reaches them, and the console and CLI access through them. */
const AccountAccessTab: React.FC<AccountAccessTabProps> = ({
  customer, account, baselineEnabled, form, canEdit, allowAdminRoles,
}) => {
  const roles: Array<[string, string | undefined]> = [
    ['Read-only role', account.read_role_name],
    ['Write role', account.write_role_name],
    ['Automation role', account.automation_role_name],
    ['Onboarding role', account.onboarding_role_name],
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <AWSConsoleButton
          customerId={customer.id}
          accountId={account.aws_account_id}
          region={account.main_region}
          allowWrite={canEdit}
        />
        <Divider orientation="vertical" flexItem />
        <AWSCliCredentials customerId={customer.id} accountId={account.aws_account_id} allowWrite={canEdit} />
      </Box>

      {canEdit ? (
        <AccessForm form={form} allowAdminRoles={allowAdminRoles} />
      ) : (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Roles">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Role name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(([use, name]) => (
              <TableRow key={use}>
                <TableCell>{use}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{name || 'Not set'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      )}

      <Typography variant="body2">
        Operations account {customer.operations_account_id || 'not configured'}
      </Typography>

      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">External ID</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {account.external_id ? (
              <>
                <Box component="span" sx={{ fontFamily: 'monospace' }}>{account.external_id}</Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {externalIdNote(account.external_id, customer.operations_account_external_id)}
                </Box>
              </>
            ) : 'Not set'}
          </Typography>
        </Box>
        {baselineEnabled && <Reachability customerId={customer.id} awsAccountId={account.aws_account_id} />}
      </Box>
    </Box>
  );
};

export default AccountAccessTab;
